import { useEffect, useRef, useState } from 'react';
import { getCorpusRoles, getTargetRoles, prewarmPresence } from './api';
import { shouldFirePresencePrewarm } from './presencePrewarmGate';

/**
 * M5 — fires POST /presence/prewarm once per mount when a signed-in user
 * with a real corpus (roles>0) AND a saved target-role family lands on a
 * screen that is one click from Generate (the Profiles/"Get Found" tab).
 * This is the Throughline-side twin of apps/web/src/pages/Presence.tsx's own
 * R125 mount effect (`if (roles.length === 0) return; ... prewarmPresence
 * (roles)`) — Presence.tsx is LOCKED and already covers its own page; this
 * hook covers the OTHER surface the M5 brief calls out ("add a prewarm fire
 * when a user with roles>0 lands anywhere generate is one click away, e.g.
 * Profiles tab mount").
 *
 * Deliberately does NOT reuse useOnboardingGate (which also reads
 * GET /corpus/roles) — that hook is mounted at App.tsx's root and its result
 * isn't threaded down to Profiles.tsx; a second, focused role-count read here
 * costs one extra GET (cheap, cache-friendly, same pattern useOnboardingGate
 * itself already justifies: "a role-count-only read keeps [this] cheap").
 *
 * Fire-and-forget + fail-quiet, matching Presence.tsx's own
 * `.catch(() => {})`: a failed corpus/target-roles read, or a failed prewarm
 * call, never surfaces an error to the user — worst case the eventual
 * Generate click just does the full uncached work itself, exactly the
 * pre-M5 behaviour.
 */
export function usePresencePrewarm(enabled: boolean): void {
  const [corpusRoleCount, setCorpusRoleCount] = useState<number | null>(null);
  const [targetRoles, setTargetRoles] = useState<string[] | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    getCorpusRoles()
      .then((roles) => {
        if (alive) setCorpusRoleCount(roles.length);
      })
      .catch(() => {
        // Unknown, not empty — mirrors useOnboardingGate's own
        // "never silently coerce a failure into a fabricated empty state."
      });
    getTargetRoles()
      .then((roles) => {
        if (alive) setTargetRoles(roles);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || fired.current) return;
    if (!shouldFirePresencePrewarm({ corpusRoleCount, targetRoles })) return;
    fired.current = true;
    // targetRoles is non-null + non-empty here (the gate just confirmed it).
    prewarmPresence(targetRoles as string[]).catch(() => {});
  }, [enabled, corpusRoleCount, targetRoles]);
}
